import React from "react";
import { useSelector } from "react-redux";
import { Bar } from "react-chartjs-2";
import { ChartData } from "chart.js";
import { useTranslation } from "react-i18next";

import { RootState } from "../../../../../store/store";
import ChartLegend from "../../ChartLegend/ChartLegend";
import { horizontalChartOptions } from "../../../../../utils/charts/chartOptions";

import theme from "../../../../../components/styles/theme/theme";
import { isRegion } from "../../../../../config";
import { EmploymentBySector } from "../../../../../features/statistics/types";

interface SectorDef {
  labelKey: string;
  system: (d: EmploymentBySector) => number;
  outlets: (d: EmploymentBySector) => number;
  suppliers: (d: EmploymentBySector) => number;
  induced: (d: EmploymentBySector) => number;
  isSystem?: boolean;
}

const SECTORS_DEFAULT: SectorDef[] = [
  { labelKey: "trade_label", system: () => 0, outlets: d => d.trade_suppliers_and_outlets, suppliers: d => d.trade_suppliers_and_outlets_suppliers, induced: () => 0 },
  { labelKey: "publicServices_label", system: () => 0, outlets: d => d.publicServices_suppliers_and_outlets, suppliers: d => d.publicServices_suppliers_and_outlets_suppliers, induced: () => 0 },
  { labelKey: "businessServices_label", system: () => 0, outlets: d => d.buesinessServices_suppliers_and_outlets, suppliers: d => d.buesinessServices_suppliers_and_outlets_suppliers, induced: () => 0 },
  { labelKey: "manufacturing_label", system: () => 0, outlets: d => d.manufacturing_suppliers_and_outlets, suppliers: d => d.manufacturing_suppliers_and_outlets_suppliers, induced: () => 0 },
  { labelKey: "transport_label", system: () => 0, outlets: d => d.transport_suppliers_and_outlets, suppliers: d => d.transport_suppliers_and_outlets_suppliers, induced: () => 0 },
  { labelKey: "system_label", system: d => d.TCCS_theCocaColaSystem, outlets: () => 0, suppliers: () => 0, induced: () => 0, isSystem: true },
  { labelKey: "other_industry_label", system: () => 0, outlets: d => d.otherIndustry_suppliers_and_outlets, suppliers: d => d.otherIndustry_suppliers_and_outlets_suppliers, induced: () => 0 },
  { labelKey: "agriculture_label", system: () => 0, outlets: d => d.agriculture_suppliers_and_outlets, suppliers: d => d.agriculture_suppliers_and_outlets_suppliers, induced: () => 0 },
];

const SECTORS_USA: SectorDef[] = [
  { labelKey: "agriculture_label", system: () => 0, outlets: d => d.agriculture_suppliers_and_outlets, suppliers: d => d.agriculture_suppliers_and_outlets_suppliers, induced: d => d.agriculture_induced ?? 0 },
  { labelKey: "businessServices_label", system: () => 0, outlets: d => d.buesinessServices_suppliers_and_outlets, suppliers: d => d.buesinessServices_suppliers_and_outlets_suppliers, induced: d => d.buesinessServices_induced ?? 0 },
  { labelKey: "construction_label", system: () => 0, outlets: d => d.construction_suppliers_and_outlets ?? 0, suppliers: d => d.construction_suppliers_and_outlets_suppliers ?? 0, induced: d => d.construction_induced ?? 0 },
  { labelKey: "foodBevManufacturing_label", system: () => 0, outlets: d => d.foodBevManufacturing_suppliers_and_outlets ?? 0, suppliers: d => d.foodBevManufacturing_suppliers_and_outlets_suppliers ?? 0, induced: d => d.foodBevManufacturing_induced ?? 0 },
  { labelKey: "foodBevRetailers_label", system: () => 0, outlets: d => d.foodBevRetailers_suppliers_and_outlets ?? 0, suppliers: d => d.foodBevRetailers_suppliers_and_outlets_suppliers ?? 0, induced: d => d.foodBevRetailers_induced ?? 0 },
  { labelKey: "otherManufacturing_label", system: () => 0, outlets: d => d.otherManufacturing_suppliers_and_outlets ?? 0, suppliers: d => d.otherManufacturing_suppliers_and_outlets_suppliers ?? 0, induced: d => d.otherManufacturing_induced ?? 0 },
  { labelKey: "otherRetailers_label", system: () => 0, outlets: d => d.otherRetailers_suppliers_and_outlets ?? 0, suppliers: d => d.otherRetailers_suppliers_and_outlets_suppliers ?? 0, induced: d => d.otherRetailers_induced ?? 0 },
  { labelKey: "otherServices_label", system: () => 0, outlets: d => d.otherServices_suppliers_and_outlets ?? 0, suppliers: d => d.otherServices_suppliers_and_outlets_suppliers ?? 0, induced: d => d.otherServices_induced ?? 0 },
  { labelKey: "packaging_label", system: () => 0, outlets: d => d.packaging_suppliers_and_outlets ?? 0, suppliers: d => d.packaging_suppliers_and_outlets_suppliers ?? 0, induced: d => d.packaging_induced ?? 0 },
  { labelKey: "restaurants_label", system: () => 0, outlets: d => d.restaurants_suppliers_and_outlets ?? 0, suppliers: d => d.restaurants_suppliers_and_outlets_suppliers ?? 0, induced: d => d.restaurants_induced ?? 0 },
  { labelKey: "system_label", system: d => d.TCCS_theCocaColaSystem, outlets: () => 0, suppliers: () => 0, induced: () => 0, isSystem: true },
  { labelKey: "transportation_label", system: () => 0, outlets: d => d.transport_suppliers_and_outlets, suppliers: d => d.transport_suppliers_and_outlets_suppliers, induced: d => d.transport_induced ?? 0 },
  { labelKey: "utilities_label", system: () => 0, outlets: d => d.utilities_suppliers_and_outlets ?? 0, suppliers: d => d.utilities_suppliers_and_outlets_suppliers ?? 0, induced: d => d.utilities_induced ?? 0 },
  { labelKey: "wholesalers_label", system: () => 0, outlets: d => d.wholesalers_suppliers_and_outlets ?? 0, suppliers: d => d.wholesalers_suppliers_and_outlets_suppliers ?? 0, induced: d => d.wholesalers_induced ?? 0 },
];

const EmploymentSectorChart: React.FC = () => {
  const { t } = useTranslation();

  const {
    statistics: { statisticsForSelectedCountry },
  } = useSelector((state: RootState) => state);

  const sectorDefs = isRegion.usa ? SECTORS_USA : SECTORS_DEFAULT;
  const bySector = statisticsForSelectedCountry?.employment_bySector;
  const hasInduced = isRegion.usa;

  // Round to nearest 100 for display; System values not rounded
  const roundJob = (v: number) => Math.round(v / 100) * 100;

  // Build per-sector data rows with rounded values; totals = sum of rounded
  const sectorRows = sectorDefs.map((def) => {
    const system = def.isSystem ? Math.round(bySector ? def.system(bySector) : 0) : roundJob(bySector ? def.system(bySector) : 0);
    const outlets = roundJob(bySector ? def.outlets(bySector) : 0);
    const suppliers = roundJob(bySector ? def.suppliers(bySector) : 0);
    const induced = roundJob(bySector && !def.isSystem ? def.induced(bySector) : 0);
    return {
      label: t(`charts.${def.labelKey}`) || "",
      system, outlets, suppliers, induced,
      total: system + outlets + suppliers + induced,
    };
  });

  // Sort by total descending
  const sorted = [...sectorRows].sort((a, b) => b.total - a.total);

  const labels = sorted.map((s) => s.label);
  const toNull = (v: number) => (v === 0 ? null : v);

  // @ts-ignore
  const data: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        label: t("charts.system_label") || "",
        data: sorted.map((s) => toNull(s.system)),
        backgroundColor: theme.palette.secondary["600"],
        minBarLength: 5,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
        order: 1,
      },
      {
        label: t("charts.first_tier_suppliers_and_outlets_label") || "",
        data: sorted.map((s) => toNull(s.outlets)),
        backgroundColor: theme.palette.secondary["200"],
        minBarLength: 5,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
        order: 2,
      },
      {
        label: t("charts.suppliers_of_suppliers_and_outlets_label") || "",
        data: sorted.map((s) => toNull(s.suppliers)),
        backgroundColor: theme.palette.secondary["50"],
        minBarLength: 5,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
        order: 3,
      },
      ...(hasInduced
        ? [
            {
              label: t("charts.induced_label") || "",
              data: sorted.map((s) => toNull(s.induced)),
              backgroundColor: theme.palette.primary["200"],
              minBarLength: 5,
              borderWidth: 1,
              borderColor: theme.palette.common.white,
              order: 4,
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <Bar options={horizontalChartOptions("employment")} data={data} />
      <ChartLegend data={data} />
    </>
  );
};

export default EmploymentSectorChart;
